import React, { forwardRef, useImperativeHandle } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

const SLIDER_WIDTH = 200;
const SLIDER_HEIGHT = 40;
const KNOB_SIZE = 30;
const HIT_SLOP = 20;

const HIGHLIGHT_COLOR = '#6370CA';
const HIGHLIGHT_BRIGHT_COLOR = '#b0b8ff';
const TEXT_COLOR = '#bdbdbd';

export interface SliderRef {
  setProgress: (progress: number) => void;
}

interface SliderProps {
  label: string;
  value: number;
  onValueChange?: (value: number) => void;
  minValue?: number;
  maxValue?: number;
}

export const AnimatedSlider = forwardRef<SliderRef, SliderProps>(
  ({ label, value, onValueChange, minValue = 0, maxValue = 1 }, ref) => {
    const translateX = useSharedValue(0);
    const contextX = useSharedValue(0);

    useImperativeHandle(ref, () => ({
      setProgress: (val: number) => {
        const progress = (val - minValue) / (maxValue - minValue);
        translateX.value = progress * (SLIDER_WIDTH - KNOB_SIZE);
      },
    }));

    const gesture = Gesture.Pan()
      .hitSlop(HIT_SLOP)
      .onStart(() => {
        contextX.value = translateX.value;
      })
      .onUpdate((e) => {
        translateX.value = Math.max(
          0,
          Math.min(SLIDER_WIDTH - KNOB_SIZE, contextX.value + e.translationX)
        );

        const progress = translateX.value / (SLIDER_WIDTH - KNOB_SIZE);
        const newValue = minValue + progress * (maxValue - minValue);
        onValueChange?.(newValue);
      });

    const progressStyle = useAnimatedStyle(() => ({
      width: translateX.value + KNOB_SIZE / 2,
      backgroundColor: HIGHLIGHT_BRIGHT_COLOR,
    }));

    const remainingStyle = useAnimatedStyle(() => ({
      width: SLIDER_WIDTH - (translateX.value + KNOB_SIZE / 2),
      backgroundColor: HIGHLIGHT_COLOR,
    }));

    const knobStyle = useAnimatedStyle(() => ({
      transform: [{ translateX: translateX.value }],
    }));

    return (
      <View style={styles.container}>
        <Text style={styles.label}>{label}</Text>
        <View style={styles.sliderContainer}>
          <View style={styles.trackContainer}>
            <Animated.View style={[styles.trackPart, progressStyle]} />
            <Animated.View style={[styles.trackPart, remainingStyle]} />
          </View>
          <View style={styles.trackHitSlop} />
          <GestureDetector gesture={gesture}>
            <Animated.View style={[styles.knob, knobStyle]} />
          </GestureDetector>
        </View>
        <Text style={styles.value}>{value.toFixed(2)}</Text>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    marginVertical: 10,
  },
  label: {
    fontSize: 16,
    marginBottom: 5,
    color: TEXT_COLOR,
  },
  sliderContainer: {
    width: SLIDER_WIDTH,
    height: SLIDER_HEIGHT,
    justifyContent: 'center',
  },
  trackContainer: {
    width: SLIDER_WIDTH,
    height: 2,
    position: 'absolute',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  trackPart: {
    height: '100%',
  },
  trackHitSlop: {
    width: SLIDER_WIDTH,
    height: HIT_SLOP,
    position: 'absolute',
    backgroundColor: 'transparent',
  },
  knob: {
    width: KNOB_SIZE,
    height: KNOB_SIZE,
    borderRadius: KNOB_SIZE / 2,
    backgroundColor: HIGHLIGHT_BRIGHT_COLOR,
    position: 'absolute',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  value: {
    fontSize: 14,
    marginTop: 5,
    color: TEXT_COLOR,
  },
});
